/**
 * Delete Account (/docs/delete-account): the public page Google Play shows
 * users, so it opens without signing in. Google asks it to name the app and
 * the developer, give the steps, and say what is deleted and what is kept.
 * What is kept is the Privacy Policy's own retention list, not a second copy.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useI18n, useLocalizedPath } from '@/lib/i18n';
import './docs.scss';

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="privacy-page__section">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function Items({ value, ordered }: { value: string; ordered?: boolean }) {
  const items = value.split('|').map((item, index) => <li key={index}>{item}</li>);
  return ordered ? <ol>{items}</ol> : <ul>{items}</ul>;
}

export function DeleteAccount() {
  const { t } = useI18n();
  const lp = useLocalizedPath();

  return (
    <div className="docs-content privacy-page">
      <h1>{t('deleteAccount.title')}</h1>
      <p>{t('deleteAccount.intro')}</p>

      <Section title={t('deleteAccount.app.title')}>
        <Items value={t('deleteAccount.app.items')} ordered />
      </Section>

      <Section title={t('deleteAccount.web.title')}>
        <Items value={t('deleteAccount.web.items')} ordered />
        <p><Link to="/dashboard/security">{t('dashboard.nav.security')}</Link></p>
      </Section>

      <Section title={t('deleteAccount.email.title')}>
        <p>{t('deleteAccount.email.content')}</p>
      </Section>

      <Section title={t('deleteAccount.deleted.title')}>
        <Items value={t('deleteAccount.deleted.items')} />
      </Section>

      <Section title={t('privacy.retention.title')}>
        <p>{t('privacy.retention.content')}</p>
        <Items value={t('privacy.retention.items')} />
        <p><Link to={lp('/docs/privacy')}>{t('privacy.title')}</Link></p>
      </Section>

      <Section title={t('deleteAccount.partial.title')}>
        <Items value={t('deleteAccount.partial.items')} />
      </Section>
    </div>
  );
}
